import { toast } from "sonner";

import { useAutoTrading } from "@/hooks";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { cn } from "@/lib/cn";

import { Button } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/skeleton";

interface AutoTradingControlButtonProps {
  /** Show text label on md+ screens */
  showText?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const config: Record<
  "stopped" | "running" | "starting" | "stopping",
  { variant: "default" | "destructive"; icon: IconName; label: string }
> = {
  stopped: {
    variant: "default",
    icon: "play",
    label: "Запустить",
  },
  running: {
    variant: "destructive",
    icon: "rounded-square",
    label: "Стоп",
  },
  starting: {
    variant: "default",
    icon: "spinner",
    label: "Запуск...",
  },
  stopping: {
    variant: "destructive",
    icon: "spinner",
    label: "Стоп...",
  },
};

export const AutoTradingControlButton = ({
  showText = true,
  className,
  size = "md",
}: AutoTradingControlButtonProps) => {
  const isMd = useBreakpoint("md");
  const showLabel = showText && isMd;

  const {
    status: autoTradingStatus,
    isLoading: isAutoTradingLoading,
    isRunning,
    start: startAutoTrading,
    stop: stopAutoTrading,
    isStarting,
    isStopping,
  } = useAutoTrading();

  const handleClick = () => {
    if (isRunning) {
      stopAutoTrading(undefined, {
        onSuccess: () => toast.success("Автоторговля остановлена"),
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : "Ошибка остановки"),
      });
    } else {
      // Проверяем что в БД есть account_uuid
      if (!autoTradingStatus?.account_uuid) {
        toast.error("Сначала выберите аккаунт для торговли");
        return;
      }

      startAutoTrading(undefined, {
        onSuccess: () => toast.success("Автоторговля запущена"),
        onError: (error) => toast.error(error instanceof Error ? error.message : "Ошибка запуска"),
      });
    }
  };

  // Определяем текущее состояние для UI
  if (isAutoTradingLoading) {
    return <Skeleton className={cn("size-10 rounded", showLabel && "h-10 w-28", className)} />;
  }

  const currentState = isStarting
    ? "starting"
    : isStopping
      ? "stopping"
      : isRunning
        ? "running"
        : "stopped";

  const statusConfig = config[currentState];
  const { variant, icon, label } = statusConfig;
  const isTransitioning = isStarting || isStopping;

  return (
    <Button
      variant={variant}
      onClick={handleClick}
      size={size}
      disabled={isTransitioning}
      buttonType={showLabel ? "default" : "icon"}
      className={className}
      icon={
        <Icon
          name={icon}
          className={cn(isTransitioning && "animate-spin")}
        />
      }
    >
      {showLabel && label}
    </Button>
  );
};
